import type { Employee, EmployeeMonthlyInfo } from '../../types/employee';
import { isDailyWorkerPay } from '../../types/employee';
import type { RegulatoryRates } from '../../types/regulatoryRates';
import {
  estimateDayInsurance,
  PENSION_HEALTH_MIN_WORK_DAYS,
  type DayInsuranceEstimate,
} from './daily-worker-insurance';
import { dayInsuranceRatesFromRegulatory } from './types';

export type DailyWorkerNoticeKind =
  | 'employmentMissingToggle'
  | 'pensionHealthSoon'
  | 'pensionHealthEligible'
  | 'employmentReminder';

export interface DailyWorkerInsuranceNotice {
  employee: Employee;
  workDays: number;
  kind: DailyWorkerNoticeKind;
  dayInsuranceEstimate: DayInsuranceEstimate | null;
  title: string;
  body: string;
  amountSummary: string | null;
  isUrgent: boolean;
}

const KIND_RANK: Record<DailyWorkerNoticeKind, number> = {
  employmentMissingToggle: 0,
  employmentReminder: 1,
  pensionHealthSoon: 2,
  pensionHealthEligible: 3,
};

function resolveNoticeKind(
  workDays: number,
  hasFourInsurance: boolean,
): DailyWorkerNoticeKind | null {
  if (workDays <= 0) return null;
  if (workDays >= PENSION_HEALTH_MIN_WORK_DAYS) return 'pensionHealthEligible';
  if (workDays === PENSION_HEALTH_MIN_WORK_DAYS - 1) return 'pensionHealthSoon';
  if (!hasFourInsurance) return 'employmentMissingToggle';
  return 'employmentReminder';
}

function formatKRW(amount: number): string {
  return `${amount.toLocaleString('ko-KR')}원`;
}

function buildNoticeCopy(
  kind: DailyWorkerNoticeKind,
  employee: Employee,
  workDays: number,
  estimate: DayInsuranceEstimate | null,
): Pick<DailyWorkerInsuranceNotice, 'title' | 'body' | 'amountSummary' | 'isUrgent'> {
  const name = employee.name;
  const daily = estimate?.dailyWage ?? Math.round(employee.dailyRate ?? 0);
  const employerDay =
    (estimate?.employerEmployment ?? 0) + (estimate?.employerIndustrial ?? 0);
  const workerDay = estimate?.workerEmployment ?? 0;
  const employerMonth = employerDay * workDays;
  const workerMonth = workerDay * workDays;

  switch (kind) {
    case 'pensionHealthEligible':
      return {
        title: `${name} — 국민·건강 가입 검토`,
        body: `이번 달 근무 ${workDays}일로, 국민연금·건강보험 가입을 검토해 보세요.`,
        amountSummary: null,
        isUrgent: true,
      };
    case 'pensionHealthSoon':
      return {
        title: `${name} — 8일 임박`,
        body: `근무 ${workDays}일입니다. 8일 이상이면 국민·건강보험 가입 검토가 필요할 수 있어요.`,
        amountSummary: employerMonth > 0
          ? `1일 기준 사업주 부담 약 ${formatKRW(employerDay)}`
          : null,
        isUrgent: false,
      };
    case 'employmentMissingToggle':
      return {
        title: `${name} — 고용·산재 신고 필요`,
        body: `4대보험 미가입 일용근로자입니다. 고용·산재보험 신고를 확인해 주세요.`,
        amountSummary:
          employerMonth > 0 || workerMonth > 0
            ? `이번 달 사업주 ${formatKRW(employerMonth)} · 근로자 ${formatKRW(workerMonth)}`
            : daily > 0
              ? `1일 기준 일당 ${formatKRW(daily)}`
              : null,
        isUrgent: true,
      };
    case 'employmentReminder':
      return {
        title: `${name} — 고용·산재 확인`,
        body: `4대보험 가입 직원의 일용근로입니다. 고용·산재보험 신고 상태를 확인해 주세요.`,
        amountSummary:
          employerMonth > 0
            ? `이번 달 사업주 부담 약 ${formatKRW(employerMonth)}`
            : null,
        isUrgent: false,
      };
    default:
      return { title: name, body: '', amountSummary: null, isUrgent: false };
  }
}

export function buildDailyWorkerNotices(
  activeEmployees: Employee[],
  monthlyInfoList: EmployeeMonthlyInfo[],
  rates: RegulatoryRates,
): DailyWorkerInsuranceNotice[] {
  const insuranceRates = dayInsuranceRatesFromRegulatory(rates);
  const infoByEmployeeId = new Map(monthlyInfoList.map((info) => [info.employee.id, info]));

  const notices: DailyWorkerInsuranceNotice[] = [];

  for (const employee of activeEmployees) {
    if (!isDailyWorkerPay(employee)) continue;

    const info = infoByEmployeeId.get(employee.id);
    const workDays = info?.workDays ?? 0;
    const kind = resolveNoticeKind(workDays, employee.hasFourInsurance);
    if (!kind) continue;

    const estimate = estimateDayInsurance(employee, insuranceRates);
    const copy = buildNoticeCopy(kind, employee, workDays, estimate);

    notices.push({
      employee,
      workDays,
      kind,
      dayInsuranceEstimate: estimate,
      ...copy,
    });
  }

  return notices.sort((a, b) => {
    if (a.isUrgent !== b.isUrgent) return a.isUrgent ? -1 : 1;
    const rankDiff = KIND_RANK[a.kind] - KIND_RANK[b.kind];
    if (rankDiff !== 0) return rankDiff;
    return b.workDays - a.workDays;
  });
}
